import { useCallback, useEffect, useState } from "react";
import { LuTrash2, LuCheckCircle2, LuAlertCircle } from "react-icons/lu";
import "../../styles/backOffice/editeEvents.css";
import EventForm from "./eventForm";
import { API_URL, authFetch } from "../../utils/api";
import { getFullWrittenDate, getStoredNow } from "../../utils/date";
import { getEventStatus, getPastEvents, getSortDate, getDisplayStart } from "../../utils/events";


//MODIFICATION ET SUPPRESSION D'UN ÉVÈNEMENT
function EditEvents() {

  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [message, setMessage] = useState(null); // { type: "success" | "error", text }

  //Liste à jour des évènements (rechargée après chaque enregistrement ou suppression)
  const loadEvents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/eventRoute/loadAllEvents`);
      setEvents(await response.json());
    } catch (error) {
      setMessage({ type: "error", text: "Impossible de charger les événements. Merci de réessayer." });
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  //ÉVÈNEMENTS PROGRAMMÉS OU EN COURS D'AFFICHAGE (du plus proche au plus lointain) PUIS PASSÉS (du plus récent au plus ancien)
  const now = getStoredNow();
  const upcomingEvents = events.filter((e) => getEventStatus(e, now) !== "past").sort((a, b) => getSortDate(a) - getSortDate(b));
  const pastEvents = getPastEvents(events, now);
  const STATUS_LABELS = { scheduled: "programmé", current: "affiché" };
  const selectedEvent = events.find((e) => e._id === selectedEventId);

  const selectEvent = (id) => {
    setSelectedEventId(id);
    setConfirmDelete(false);
    setMessage(null);
  };

  // ******************* ENREGISTREMENT *******************
  const updateEvent = async (eventData) => {
    const response = await authFetch(`/eventRoute/updateEvent?_id=${selectedEventId}`, {
      method: "PUT",
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData),
    });
    const body = await response.json();
    if (body.result === true) {
      loadEvents();
    }
    return body;
  };

  // ******************* SUPPRESSION (avec confirmation) *******************
  const deleteEvent = async () => {
    setIsDeleting(true);
    try {
      const response = await authFetch(`/eventRoute/deleteEvent?_id=${selectedEventId}`, { method: "DELETE" });
      const body = await response.json();
      if (body.result === true) {
        setSelectedEventId("");
        setMessage({ type: "success", text: `« ${selectedEvent.title} » a bien été supprimé.` });
        loadEvents();
      } else {
        setMessage({ type: "error", text: body.error.join(" ") });
      }
    } catch (error) {
      setMessage({ type: "error", text: "L’événement n’a pas pu être supprimé. Merci de réessayer." });
    }
    setConfirmDelete(false);
    setIsDeleting(false);
  };

  const deleteActions = confirmDelete
    ? <div className="delete_confirm" role="alert">
      <p>Supprimer définitivement cet événement et son affiche ?</p>
      <button type="button" className="btn btn--danger" onClick={deleteEvent} disabled={isDeleting}>
        {isDeleting ? "Suppression…" : "Oui, supprimer"}
      </button>
      <button type="button" className="btn btn--secondary" onClick={() => setConfirmDelete(false)} disabled={isDeleting}>Annuler</button>
    </div>
    : <button type="button" className="btn btn--secondary btn--danger-outline" onClick={() => setConfirmDelete(true)}>
      <LuTrash2 aria-hidden="true" /> Supprimer l’événement
    </button>;

  //Libellé : titre, date de l'évènement (ou de début d'affichage) et état d'affichage
  const eventOption = (e) => {
    const status = getEventStatus(e, now);
    return <option key={e._id} value={e._id}>{e.title} — {getFullWrittenDate(e.startDate || getDisplayStart(e))}{STATUS_LABELS[status] ? ` (${STATUS_LABELS[status]})` : ""}</option>;
  };


  return (
    <div className="bo-page surface">
      <h2 className="title-xl bo-page_title">Modifier un événement</h2>
      <p className="bo-page_intro">Choisissez un événement pour modifier tout ou partie de ses informations, ou le supprimer.</p>

      {/* SÉLECTION DE L'ÉVÈNEMENT */}
      <div className="field edit_event_select">
        <label className="field_label" htmlFor="eventSelect">Événement</label>
        <select className="field_control" id="eventSelect" value={selectedEventId} onChange={(e) => selectEvent(e.target.value)}>
          <option value="">{events.length > 0 ? "Sélectionner un événement" : "Aucun événement enregistré"}</option>
          {upcomingEvents.length > 0 && <optgroup label="Programmés ou affichés">{upcomingEvents.map(eventOption)}</optgroup>}
          {pastEvents.length > 0 && <optgroup label="Passés">{pastEvents.map(eventOption)}</optgroup>}
        </select>
      </div>

      {message && (
        <div className={`msg ${message.type === "success" ? "msg--success" : "msg--error"} edit_event_message`} role="status">
          {message.type === "success" ? <LuCheckCircle2 aria-hidden="true" /> : <LuAlertCircle aria-hidden="true" />}
          <p>{message.text}</p>
        </div>
      )}

      {/* FORMULAIRE PRÉ-REMPLI (réinitialisé à chaque changement d'évènement grâce à la "key") */}
      {selectedEvent && (
        <EventForm
          key={selectedEvent._id}
          initialEvent={selectedEvent}
          onSubmit={updateEvent}
          submitLabel="Enregistrer les modifications"
          extraActions={deleteActions}
        />
      )}
    </div>
  )
};

export default EditEvents;
